import React, { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { Vector3 } from 'three';
import { EAttributeType, EAttributeUnit } from '../@types/AttributeTypes';
import AttachmentModal from '../components/modals/AttachmentModal';
import AttachmentModel, { AttachmentType } from '../models/standard/AttachmentModel';
import AttributeModel from '../models/standard/AttributeModel';
import CameraPositionSpriteModel from '../models/standard/CameraPositionSpriteModel';
import HotspotModel from '../models/standard/HotspotModel';
import SimpleMeasurementModel from '../models/standard/SimpleMeasurementModel';
import {
  convertAttributeValue,
  getCorrespondingUnit,
  IMPERIAL_TO_METRIC,
  METRIC_SCALE_FACTORS // Ensure this is exported from attributeUtils
} from '../utils/attributes/attributeUtils';
import { isRegionImperial } from '../utils/regionUtils';
import { useUserDataContext } from './UserDataProvider';

interface AnnotationsContextType {
  hotspots: HotspotModel[];
  measurements: SimpleMeasurementModel[];
  cameraPositionSprites: CameraPositionSpriteModel[];
  addHotspot: (data: { position: Vector3; userId?: string | null } | HotspotModel | null) => void;
  addMeasurement: (measurement: SimpleMeasurementModel) => void;
  addCameraPositionSprite: (cameraPositionSprite: CameraPositionSpriteModel) => void;
  updateHotspotAttachment: (id: number, newAttachment: AttachmentModel, position?: Vector3) => void;
  updateMeasurementColor: (id: number, color: string) => void;
  updateMeasurementPosition: (id: number, startPoint: Vector3, endPoint: Vector3 | null) => void;
  deleteHotspot: (id: number) => void;
  deleteMeasurement: (id: number) => void;
  resetAnnotations: () => void;
  setPendingAttachment: (attachment: AttachmentModel | null) => void;
  convertAnnotationsToRegion: (targetRegion: string) => void;
  updateCameraPositionSprites: (fn: (prev: CameraPositionSpriteModel[]) => CameraPositionSpriteModel[]) => void;
  getCameraPositionSpritesSnapshot: () => CameraPositionSpriteModel[];
  deleteCameraPositionSprite: (arg: number | string | { id?: number; uuid?: string }) => void;
}

const AnnotationsContext = createContext<AnnotationsContextType>({
  hotspots: [],
  measurements: [],
  cameraPositionSprites: [],
  addHotspot: () => {},
  addMeasurement: () => {},
  addCameraPositionSprite: () => {},
  updateHotspotAttachment: () => {},
  updateMeasurementColor: () => {},
  updateMeasurementPosition: () => {},
  deleteHotspot: () => {},
  deleteMeasurement: () => {},
  resetAnnotations: () => {},
  setPendingAttachment: () => {},
  convertAnnotationsToRegion: () => {},
  updateCameraPositionSprites: () => {},
  getCameraPositionSpritesSnapshot: () => [],
  deleteCameraPositionSprite: () => {}
});

export const useAnnotations = () => useContext(AnnotationsContext);

interface AnnotationsProviderProps {
  children: ReactNode;
}

export const AnnotationsProvider: React.FC<AnnotationsProviderProps> = ({ children }) => {
  const [hotspots, setHotspots] = useState<HotspotModel[]>([]);
  const [measurements, setMeasurements] = useState<SimpleMeasurementModel[]>([]);
  const [cameraPositionSprites, setCameraPositionSprites] = useState<CameraPositionSpriteModel[]>([]);
  const cameraPositionSpritesRef = useRef<CameraPositionSpriteModel[]>([]);
  const [activeHotspotId, setActiveHotspotId] = useState<number | null>(null);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isHotspotSaved, setIsHotspotSaved] = useState(false);
  const [pendingHotspotPosition, setPendingHotspotPosition] = useState<Vector3 | null>(null);
  const [pendingAttachment, setPendingAttachment] = useState<AttachmentModel | null>(null);

  const { currentUserData } = useUserDataContext();

  const applyCameraPositionSprites = useCallback((fn: (prev: CameraPositionSpriteModel[]) => CameraPositionSpriteModel[]) => {
    const nextCameraPositionSprites = fn(cameraPositionSpritesRef.current);
    cameraPositionSpritesRef.current = nextCameraPositionSprites;
    setCameraPositionSprites(nextCameraPositionSprites);
  }, []);

  const addCameraPositionSprite = useCallback(
    (cameraPositionSprite: CameraPositionSpriteModel) => {
      applyCameraPositionSprites((prevCameraPositionSprites) => [...prevCameraPositionSprites, cameraPositionSprite]);
    },
    [applyCameraPositionSprites],
  );

  const updateCameraPositionSprites = useCallback(
    (fn: (prev: CameraPositionSpriteModel[]) => CameraPositionSpriteModel[]) => {
      applyCameraPositionSprites(fn);
    },
    [applyCameraPositionSprites],
  );

  const getCameraPositionSpritesSnapshot = useCallback(() => cameraPositionSpritesRef.current, []);
  
  const deleteCameraPositionSprite = useCallback(
    (arg: number | string | { id?: number; uuid?: string }) => {
      applyCameraPositionSprites((prev) => {
        const id =
          typeof arg === 'number' ? arg :
          typeof arg === 'string' ? undefined :
          arg?.id;
  
        const uuid =
          typeof arg === 'string' ? arg :
          typeof arg === 'number' ? undefined :
          arg?.uuid;
  
        return (prev ?? []).filter((s: any) => {
          if (uuid && s?.uuid) return s.uuid !== uuid;
          if (id !== undefined && id !== null) return s?.id !== id;
          return true;
        });
      });
    },
    [applyCameraPositionSprites],
  );

  const addHotspot = (data: { position: Vector3; userId?: string | null } | HotspotModel | null) => {
    if (!data) {
      setIsAttachmentModalOpen(true);
      setActiveHotspotId(0);
      return;
    }

    if (!(data instanceof HotspotModel)) {
      if (!pendingAttachment) {
        console.warn('No pending attachment selected before placing hotspot.');
        return;
      }

      const userDataId = data.userId?.trim() ? data.userId : currentUserData.id || null;
      const newId = hotspots.length + 1;

      const newHotspot = new HotspotModel(newId, data.position, pendingAttachment, new Date(), userDataId);

      setHotspots((prev) => [...prev, newHotspot]);

      setPendingAttachment(null);
      setIsAttachmentModalOpen(false);
      setActiveHotspotId(null);
      setIsHotspotSaved(false);
      setPendingHotspotPosition(null);
      return;
    }

    setHotspots((prev) => [...prev, data]);
    setActiveHotspotId(data.id);
    setIsHotspotSaved(false);
  };

  const updateHotspotAttachment = (id: number, newAttachment: AttachmentModel, position?: Vector3) => {
    setHotspots((prevHotspots) => {
      if (id === 0 && position) {
        const newId = prevHotspots.length + 1;
        const newHotspot = new HotspotModel(newId, position, newAttachment, new Date(), currentUserData.id || null);
        return [...prevHotspots, newHotspot];
      }
      const updatedHotspots = prevHotspots.map((h) => {
        if (h.id === id) {
          return new HotspotModel(h.id, h.position, newAttachment, h.creationDate, h.userId);
        }
        return h;
      });
      return updatedHotspots;
    });
    setActiveHotspotId(null);
    setIsAttachmentModalOpen(false);
    setPendingHotspotPosition(null);
  };

  const deleteHotspot = (id: number) => {
    setHotspots((prevHotspots) => {
      const updatedHotspots = prevHotspots.filter((hotspot) => hotspot.id !== id);
      const renumberedHotspots = updatedHotspots.map((hotspot, index) => new HotspotModel(index + 1, hotspot.position, hotspot.attachment, hotspot.creationDate, hotspot.userId));
      return renumberedHotspots;
    });
    setActiveHotspotId(null);
    setIsAttachmentModalOpen(false);
  };

  const addMeasurement = (measurement: SimpleMeasurementModel) => {
    setMeasurements((prevMeasurements) => [...prevMeasurements, measurement]);
  };

  const deleteMeasurement = (id: number) => {
    setMeasurements((prevMeasurements) => {
      const updatedMeasurements = prevMeasurements.filter((m) => m.id !== id);
      const renumberedMeasurements = updatedMeasurements.map(
        (measurement, index) =>
          new SimpleMeasurementModel(
            index + 1,
            measurement.startPoint,
            measurement.endPoint,
            measurement.color,
            measurement.userId,
            measurement.author?.split(' ')[0] || '',
            measurement.author?.split(' ')[1] || '',
            measurement.creationDate,
          ),
      );
      return renumberedMeasurements;
    });
  };

  const updateMeasurementColor = (id: number, color: string) => {
    setMeasurements((prevMeasurements) =>
      prevMeasurements.map((m) => {
        if (m.id === id) {
          m.updateMeasurementColor(color);
        }
        return m;
      }),
    );
  };

  const updateMeasurementPosition = (id: number, startPoint: Vector3, endPoint: Vector3 | null) => {
    setMeasurements((prevMeasurements) =>
      prevMeasurements.map((m) => {
        if (m.id === id) {
          m.updateMeasurementPosition(startPoint, endPoint);
        }
        return m;
      }),
    );
  };

  const resetAnnotations = () => {
    setHotspots([]);
    setMeasurements([]);
    applyCameraPositionSprites(() => []);
    setActiveHotspotId(null);
    setIsAttachmentModalOpen(false);
    setIsHotspotSaved(false);
    setPendingHotspotPosition(null);
    setPendingAttachment(null);
  };

  const handleModalClose = () => {
    setIsAttachmentModalOpen(false);
    setActiveHotspotId(null);
    setPendingHotspotPosition(null);
    setPendingAttachment(null);
  };

  // --- UPDATED: Convert with Magnitude Correction ---
  const convertAnnotationsToRegion = (targetRegion: string) => {
    const toImperial = isRegionImperial(targetRegion);

    setHotspots((prevHotspots) => {
      return prevHotspots.map((hotspot) => {
        // Only convert if it's an ATTRIBUTE attachment
        if (hotspot.attachment.type !== AttachmentType.ATTRIBUTE) {
            return hotspot;
        }
        
        const attr = hotspot.attachment.data as AttributeModel;
        
        // Only convert NUMBER types with a Unit
        if (attr.type !== EAttributeType.NUMBER || !attr.unit) {
            return hotspot;
        }

        const currentUnit = attr.unit as EAttributeUnit;
        const targetUnit = getCorrespondingUnit(currentUnit, toImperial);

        if (currentUnit === targetUnit) {
            return hotspot;
        }

        const rawVal = parseFloat(String(attr.value));
        if (isNaN(rawVal)) return hotspot;

        // 1. Basic Conversion (Metric -> Imperial OR Imperial -> Base Metric)
        let convertedVal = convertAttributeValue(rawVal, currentUnit, toImperial);

        // 2. Magnitude Fix (Imperial -> Metric)
        // convertAttributeValue returns Meters. Divide by target scale (e.g. 0.01 for CM)
        if (!toImperial && IMPERIAL_TO_METRIC[currentUnit]) {
            const scale = METRIC_SCALE_FACTORS[targetUnit] || 1;
            convertedVal = convertedVal / scale;
        }

        const roundedVal = Math.round(convertedVal * 10000) / 10000;

        const newAttr = new AttributeModel(
          attr.id,
          attr.key,
          roundedVal,
          attr.predecessorId,
          attr.type,
          targetUnit, // New Unit
          attr.hidden,
          attr.forced,
          attr.ownerId,
          attr.userId,
          attr.publicFlag,
          attr.localization,
          attr.defaults,
          attr.validation,
          attr.unitFamily
        );

        return new HotspotModel(
            hotspot.id, 
            hotspot.position, 
            new AttachmentModel(AttachmentType.ATTRIBUTE, newAttr), 
            hotspot.creationDate, 
            hotspot.userId
        );
      });
    });
  };

  return (
    <AnnotationsContext.Provider
      value={{
        hotspots,
        measurements,
        cameraPositionSprites,
        addHotspot,
        addMeasurement,
        addCameraPositionSprite,
        updateHotspotAttachment,
        updateMeasurementColor,
        updateMeasurementPosition,
        deleteHotspot,
        deleteMeasurement,
        resetAnnotations,
        setPendingAttachment,
        convertAnnotationsToRegion,
        updateCameraPositionSprites,
        getCameraPositionSpritesSnapshot,
        deleteCameraPositionSprite
      }}
    >
      {children}
      <AttachmentModal isOpen={isAttachmentModalOpen} onClose={handleModalClose} />
    </AnnotationsContext.Provider>
  );
};

export default AnnotationsProvider;
